export type AppThemeMode = "light" | "dark";

export interface BrandPalette {
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
}

export interface BrandConfig {
  light: BrandPalette;
  dark: BrandPalette;
  fontFamily: string;
}

export const staticBrandConfig: BrandConfig = {
  light: {
    primaryColor: "#0A5BBA",
    secondaryColor: "#FF6D41",
    textColor: "#1F2937",
  },
  dark: {
    primaryColor: "#60A5FA",
    secondaryColor: "#FB923C",
    textColor: "#F3F4F6",
  },
  fontFamily: "Montserrat",
};

const BRANDING_STORAGE_KEY = "app-brand-config";

export const getBrandConfigFromStorage = (): BrandConfig | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(BRANDING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<BrandConfig>;

    const hasLight =
      typeof parsed.light === "object" &&
      parsed.light !== null &&
      typeof (parsed.light as Partial<BrandPalette>).primaryColor === "string" &&
      typeof (parsed.light as Partial<BrandPalette>).secondaryColor === "string" &&
      typeof (parsed.light as Partial<BrandPalette>).textColor === "string";

    const hasDark =
      typeof parsed.dark === "object" &&
      parsed.dark !== null &&
      typeof (parsed.dark as Partial<BrandPalette>).primaryColor === "string" &&
      typeof (parsed.dark as Partial<BrandPalette>).secondaryColor === "string" &&
      typeof (parsed.dark as Partial<BrandPalette>).textColor === "string";

    if (hasLight && hasDark && typeof parsed.fontFamily === "string") {
      return {
        light: {
          primaryColor: (parsed.light as BrandPalette).primaryColor,
          secondaryColor: (parsed.light as BrandPalette).secondaryColor,
          textColor: (parsed.light as BrandPalette).textColor,
        },
        dark: {
          primaryColor: (parsed.dark as BrandPalette).primaryColor,
          secondaryColor: (parsed.dark as BrandPalette).secondaryColor,
          textColor: (parsed.dark as BrandPalette).textColor,
        },
        fontFamily: parsed.fontFamily,
      };
    }

    return null;
  } catch {
    return null;
  }
};

export const saveBrandConfigToStorage = (config: BrandConfig): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(config));
};

const asString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const mapApiResponseToBrandConfig = (payload: any): BrandConfig | null => {
  const shellColors = payload?.colors;
  const shellFonts = payload?.fonts;
  const shellFontFamily = asString(shellFonts?.primary);
  const shellPrimary = asString(shellColors?.primary);
  const shellSecondary = asString(shellColors?.secondary);
  const shellText = asString(shellColors?.text);

  if (shellFontFamily && shellPrimary && shellSecondary && shellText) {
    return {
      light: {
        primaryColor: shellPrimary,
        secondaryColor: shellSecondary,
        textColor: shellText,
      },
      dark: {
        primaryColor: shellPrimary,
        secondaryColor: shellSecondary,
        textColor: shellText,
      },
      fontFamily: shellFontFamily,
    };
  }

  const candidates = [
    payload,
    payload?.data,
    payload?.result,
    Array.isArray(payload) ? payload[0] : null,
    Array.isArray(payload?.data) ? payload.data[0] : null,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const source = candidate?.theme || candidate;

    const fontFamily = asString(source?.fontFamily);
    const lightPrimary = asString(source?.light?.primaryColor) ?? asString(source?.primaryColor);
    const lightSecondary = asString(source?.light?.secondaryColor) ?? asString(source?.secondaryColor);
    const lightText = asString(source?.light?.textColor) ?? asString(source?.textColor);

    const darkPrimary = asString(source?.dark?.primaryColor) ?? lightPrimary;
    const darkSecondary = asString(source?.dark?.secondaryColor) ?? lightSecondary;
    const darkText = asString(source?.dark?.textColor) ?? lightText;

    if (!fontFamily || !lightPrimary || !lightSecondary || !lightText) {
      continue;
    }

    return {
      light: {
        primaryColor: lightPrimary,
        secondaryColor: lightSecondary,
        textColor: lightText,
      },
      dark: {
        primaryColor: darkPrimary || lightPrimary,
        secondaryColor: darkSecondary || lightSecondary,
        textColor: darkText || lightText,
      },
      fontFamily,
    };
  }

  return null;
};
